// The exercise stream: after any attempt (or from a lesson), the tutor writes the next exercise for this learner.
// Lessons are starting points; the stream continues as long as the learner wants. No scheduling, no scores.
import { z } from 'zod';
import { Checkpoint } from './lesson.ts';
import { PracticeDemand } from './learning.ts';

/** How the next exercise relates to what came before. */
export const StreamMove = z.enum(['variant', 'stretch', 'elaborate', 'drill', 'predict', 'redraw', 'step-back']);

export const Exercise = z.object({
  id: z.string(),
  lessonId: z.string(),
  /** The attempt it follows; null when it opened the lesson's stream. */
  afterAttemptId: z.string().nullable(),
  move: StreamMove,
  /** One line from the tutor: why this exercise now. */
  why: z.string().max(400),
  checkpoint: Checkpoint,
  createdAt: z.string(),
  /** Latest attempt on it, if any. */
  status: z.enum(['new', 'open', 'done']),
  sittingId: z.string().nullable().optional(),
  demand: PracticeDemand.optional(),
  retestOf: z.string().nullable().optional(),
});

/** POST /api/exercises. afterAttemptId must be the learner's own attempt in that lesson. */
export const NextExerciseReq = z.object({
  lessonId: z.string(),
  afterAttemptId: z.string().nullable(),
  /** Optional steer typed by the learner ("harder", "more on the scheduler"). */
  ask: z.string().max(400).nullable().optional(),
  sittingId: z.string().nullable().optional(),
});
export const StreamResponse = z.object({ exercises: z.array(Exercise) });
/** GET /api/stream/recent: the learner's latest exercises across lessons, newest first, to pick the stream back up. */
export const RecentStreamResponse = z.object({ exercises: z.array(Exercise.extend({ lessonTitle: z.string() })) });

/** The tutor's plain-text note about the learner (Ari's USER.md): readable and editable by the learner. */
export const LearnerNotes = z.object({ text: z.string().max(4000), updatedAt: z.string().nullable() });
export const LearnerNotesReq = z.object({ text: z.string().max(4000) });

export type StreamMove = z.infer<typeof StreamMove>;
export type Exercise = z.infer<typeof Exercise>;
export type NextExerciseReq = z.infer<typeof NextExerciseReq>;
export type StreamResponse = z.infer<typeof StreamResponse>;
export type RecentStreamResponse = z.infer<typeof RecentStreamResponse>;
export type LearnerNotes = z.infer<typeof LearnerNotes>;
